import React from "react";
import { FileText, Printer, CheckCircle2, Shield } from "lucide-react";

export interface DigitalLetterheadPreviewProps {
  companyName?: string;
  companyNameEn?: string;
  address?: string;
  taxCode?: string;
  phone?: string;
  email?: string;
  website?: string;
  bankInfo?: string;
  logoUrl?: string;
  className?: string;
}

export const DigitalLetterheadPreview: React.FC<DigitalLetterheadPreviewProps> = ({
  companyName = "CÔNG TY TNHH ARTPRESTO VIỆT NAM",
  companyNameEn = "ARTPRESTO VIETNAM CO., LTD",
  address = "Lô CN09, Đường số 1, KCN Hải Sơn, Xã Đức Hòa Đông, Huyện Đức Hòa, Tỉnh Long An",
  taxCode = "0700820294",
  phone = "0226 246 0988",
  email = "info@artpresto.vn",
  website = "https://artpresto.vn",
  bankInfo = "MUFG Bank, Ltd. - CN Hà Nội | STK: 040436",
  logoUrl,
  className = "",
}) => {
  return (
    <div className={`space-y-3 ${className}`}>
      <div className="flex items-center justify-between text-xs text-slate-500">
        <span className="font-semibold uppercase tracking-wider text-[11px] text-slate-400 flex items-center gap-1.5">
          <Printer className="w-3.5 h-3.5 text-indigo-500" />
          Xem trước Mẫu Tiêu đề in A4 (Letterhead)
        </span>
        <span className="inline-flex items-center gap-1 text-[10px] font-medium text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded border border-emerald-200">
          
        </span>
      </div>

      {/* Miniature A4 Paper Container */}
      <div className="relative w-full rounded-xl bg-white border border-slate-200/90 shadow-sm p-4 text-slate-800 transition-all hover:shadow-md overflow-hidden">
        {/* Paper top accent */}
        <div className="h-1 -mx-4 -mt-4 bg-gradient-to-r from-indigo-600 via-sky-500 to-indigo-800" />

        {/* Letterhead Header Section */}
        <div className="flex items-start gap-3 pt-2 pb-3 border-b-2 border-slate-800/80">
          {/* Logo */}
          <div className="w-12 h-12 shrink-0 rounded-lg border border-slate-100 bg-slate-50 flex items-center justify-center p-1 overflow-hidden">
            {logoUrl ? (
              <img
                src={logoUrl}
                alt="Logo"
                className="max-h-full max-w-full object-contain"
                onError={(e) => (e.currentTarget.style.display = "none")}
              />
            ) : (
              <div className="text-[11px] font-black text-indigo-700 tracking-tighter text-center">
                APV
              </div>
            )}
          </div>

          {/* Company Legal Info in Micro-typography */}
          <div className="flex-1 min-w-0">
            <h4 className="text-[12px] sm:text-[13px] font-black uppercase text-slate-900 tracking-tight leading-tight break-words">
              {companyName}
            </h4>
            {companyNameEn && (
              <p className="text-[10px] font-semibold text-slate-500 uppercase tracking-tight break-words">
                {companyNameEn}
              </p>
            )}
            <div className="mt-1 space-y-0.5 text-[9.5px] leading-tight text-slate-600">
              <p className="break-words">
                <span className="font-semibold text-slate-700">Trụ sở:</span> {address}
              </p>
              <div 
                className="flex flex-wrap items-center gap-x-2 text-slate-600"
                style={{ overflowWrap: "anywhere", wordBreak: "normal" }}
              >
                <span>
                  <span className="font-semibold text-slate-700">MST:</span> {taxCode}
                </span>
                <span>•</span>
                <span>
                  <span className="font-semibold text-slate-700">ĐT:</span> {phone}
                </span>
                {email && (
                  <>
                    <span>•</span>
                    <span>
                      <span className="font-semibold text-slate-700">Email:</span> {email}
                    </span>
                  </>
                )}
              </div>
              {bankInfo && (
                <p className="break-words text-slate-500">
                  <span className="font-semibold text-slate-700">TK Ngân hàng:</span> {bankInfo}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Simulated Document Body Lines (Mock Quotation/PO) */}
        <div className="pt-3 pb-2 space-y-1.5 opacity-60">
          <div className="flex items-center justify-between pb-1">
            <div className="h-2 w-28 bg-indigo-200/70 rounded" />
            <div className="h-2 w-16 bg-slate-200 rounded" />
          </div>
          <div className="h-1.5 w-full bg-slate-100 rounded" />
          <div className="h-1.5 w-5/6 bg-slate-100 rounded" />
          <div className="h-1.5 w-4/6 bg-slate-100 rounded" />
          <div className="grid grid-cols-4 gap-1 pt-1">
            <div className="h-5 bg-slate-50 border border-slate-100 rounded" />
            <div className="h-5 bg-slate-50 border border-slate-100 rounded" />
            <div className="h-5 bg-slate-50 border border-slate-100 rounded" />
            <div className="h-5 bg-slate-50 border border-slate-100 rounded" />
          </div>
        </div>

        {/* Certified Badge Footnote */}
        <div className="mt-2 pt-2 border-t border-dashed border-slate-200 flex items-center justify-between text-[9px] text-slate-400">
          <span className="flex items-center gap-1">
            <Shield className="w-3 h-3 text-indigo-500" /> Bản quyền biểu mẫu ISO APV-ERP
          </span>
          <span className="font-mono">Khổ giấy tiêu chuẩn A4 (210 × 297 mm)</span>
        </div>
      </div>
    </div>
  );
};
export default DigitalLetterheadPreview;
